'use client';

import { useEffect, useState } from 'react';

import classnames from 'classnames';
import { es } from 'date-fns/locale/es';
import { default as ReactDatePicker, registerLocale } from 'react-datepicker';

import { getUTCTimezoneOffset } from '../../utils/date';
import { Button } from '../button';
import { Popover } from '../popover';
import { PopoverContent, PopoverTrigger } from '../popover/Popover';
import type { SingleDatePickerProps } from './SingleDatePicker.types';
import { DATE_PICKER_LABELS } from './labels';
import { TimePicker } from './layout/TimePicker';

// sort-imports-ignore
import './react-datepicker-vendor.scss';
import styles from './DatePicker.module.scss';

registerLocale('es', es);

const MINUTE_MS = 60000;

function SingleDatePicker({
  style,
  className,
  onSubmit,
  onChange,
  initDate = new Date(),
  isSubmitting = false,
  labels,
  locale = 'en',
  hideTime = false,
  closeOnDateClick = false,
  maxDate,
  testId = 'ui-single-date-picker',
}: SingleDatePickerProps) {
  const normalizeDate = (date: Date) =>
    hideTime
      ? new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0)
      : date;

  const [date, setDate] = useState<Date | undefined>(normalizeDate(initDate));
  const text = { ...DATE_PICKER_LABELS[locale], ...labels };

  const [timezoneOffset, setTimezoneOffset] = useState<string>('UTC');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimezoneOffset(getUTCTimezoneOffset(new Date()));
    }, MINUTE_MS);

    setTimezoneOffset(getUTCTimezoneOffset(new Date()));

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (onChange && date) {
      onChange(date);
    }
  }, [date]);

  useEffect(() => {
    setDate(normalizeDate(initDate));
  }, [initDate]);

  const formatDate = (date: Date, locale: string) => {
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Popover placement="bottom" open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild={true} onClick={() => setOpen((v) => !v)}>
        <Button
          style={style}
          className={className}
          variant="outline-primary"
          data-testid={`${testId}_selector`}
        >
          {formatDate(new Date(date ?? new Date()), locale)}
        </Button>
      </PopoverTrigger>
      <PopoverContent className={styles['popover-content']}>
        <div className={styles['container']}>
          <div className={classnames(styles['content'], styles[`content--lg`])}>
            <div
              className={classnames(
                styles['disposition'],
                styles[`disposition--lg`],
              )}
            >
              <div
                className={classnames(styles['wrapper'])}
                data-testid={`${testId}_fromDatePicker`}
              >
                <ReactDatePicker
                  selected={date}
                  onChange={(date) => {
                    if (date) {
                      setDate(
                        new Date(
                          date.getFullYear(),
                          date.getMonth(),
                          date.getDate(),
                          0,
                          0,
                          0,
                        ),
                      );

                      if (closeOnDateClick) {
                        setOpen(false);
                      }
                    }
                  }}
                  inline={true}
                  disabledKeyboardNavigation
                  showPopperArrow={false}
                  locale={locale}
                  maxDate={maxDate}
                />
                {!hideTime && (
                  <TimePicker
                    testId={`${testId}_fromTimePicker`}
                    date={date ?? new Date()}
                    minDate={
                      date
                        ? new Date(
                            date.getFullYear(),
                            date.getMonth(),
                            date.getDate(),
                            0,
                            0,
                            0,
                          )
                        : new Date()
                    }
                    maxDate={
                      date
                        ? new Date(
                            date.getFullYear(),
                            date.getMonth(),
                            date.getDate(),
                            23,
                            59,
                            59,
                          )
                        : new Date()
                    }
                    onChange={(date: Date) => {
                      setDate(date);
                    }}
                  />
                )}
              </div>
            </div>
          </div>
          {onSubmit && (
            <div className={classnames(styles['footer'], styles[`footer--lg`])}>
              <span className={styles['footer__utc']}>{timezoneOffset}</span>
              <div
                className={classnames(
                  styles['footer__button'],
                  styles[`footer__button--lg`],
                )}
              >
                <Button
                  variant="primary"
                  disabled={isSubmitting}
                  loading={isSubmitting}
                  onClick={() => {
                    if (onSubmit) {
                      onSubmit(date ?? new Date());
                    }
                    setOpen(false);
                  }}
                  data-testid={`${testId}_submit`}
                >
                  {text.submit}
                </Button>
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { SingleDatePicker };
